/**
 * Slash-command palette for VibeKonnect chat input.
 *
 * Each command is { key, description, action(ctx) } where ctx exposes:
 *   ctx.setPrompt(text)     — replace input text
 *   ctx.sendPrompt(text)    — replace + immediately send
 *   ctx.navigate(path)      — router push
 *   ctx.openPanel(id)       — switch right-panel tab
 *   ctx.handleReset()       — clear session
 *   ctx.showToast(msg)      — sonner toast
 */
export const SLASH_COMMANDS = [
  {
    key: '/plan',
    description: 'Re-plan the current task with the Architect agent.',
    action: (ctx) => ctx.sendPrompt('@architect Re-plan this. Restate the goal, list concrete tasks, call out what is out of scope. No code yet.'),
  },
  {
    key: '/debug',
    description: 'Hand the latest error to the QA agent for autonomous fix.',
    action: (ctx) => ctx.sendPrompt('@qa Investigate the most recent error. Reproduce, root-cause, and propose a fix. Apply if you are confident.'),
  },
  {
    key: '/deploy',
    description: 'Commit staged files to GitHub AND trigger production rebuild.',
    action: (ctx) => { ctx.deploy?.(); ctx.showToast?.('Deploy triggered — check the commit bar for progress.'); },
  },
  {
    key: '/preview',
    description: 'Apply staged files to preview iframe without committing.',
    action: (ctx) => { ctx.preview?.(); ctx.showToast?.('Applying staged files to preview…'); },
  },
  {
    key: '/rollback',
    description: 'Open the version-history panel to restore a prior state.',
    action: (ctx) => ctx.sendPrompt('/rollback — show me the last 10 VibeKonnect commits so I can pick one to revert to.'),
  },
  {
    key: '/fork',
    description: 'Fork this chat into a new thread with full project context.',
    action: (ctx) => { ctx.fork?.(); ctx.showToast?.('Forking thread…'); },
  },
  {
    key: '/reset',
    description: 'Clear this session (keeps persistent memory).',
    action: (ctx) => { ctx.handleReset?.(); },
  },
  {
    key: '/clear-memory',
    description: 'Wipe persistent chat memory across all sessions.',
    action: (ctx) => { ctx.clearMemory?.(); },
  },
  {
    key: '/cost',
    description: 'Show token usage and USD cost for this session.',
    action: (ctx) => { ctx.showCost?.(); },
  },
  {
    key: '/audit',
    description: 'Open the VibeKonnect self-audit report.',
    action: (ctx) => ctx.navigate?.('/dev-console#audit'),
  },
  {
    key: '/docs',
    description: 'Generate INTEGRATION_GUIDE, API_REFERENCE, and ARCHITECTURE docs from the live codebase.',
    action: (ctx) => ctx.sendPrompt(
      '@architect Generate three production-grade markdown documents from the live codebase and save each to the vibeconnect artifacts store via the PUT /api/dev-console/vibeconnect/artifacts/{name} endpoint (or via write_file to a suitable path if the artifacts endpoint is not tool-accessible):\n\n' +
      '1. **INTEGRATIONS.md** — every 3rd-party integration currently wired (Stripe / PayPal / Anthropic / OpenAI / SendGrid / Twilio / GitHub / Kaharian Gateway / Emergent LLM). For each: env vars required, official docs URL, one-line description of what Konnect uses it for, and the exact file where the integration lives.\n' +
      '2. **API_REFERENCE.md** — group backend routes by module (auth, ai, payments, dev-console, vibeconnect, etc.). For each route: method, path, minimum role, brief description, request body shape (if any), response shape.\n' +
      '3. **ARCHITECTURE.md** — high-level system: frontend stack, backend stack, DB (MongoDB + collections), auth flow, deploy pipeline (GitHub → Plesk with the new remote-build verification), VibeKonnect module boundaries, environment matrix (preview vs production).\n\n' +
      'Keep each doc concise (≤ 200 lines). Use ONLY facts discoverable in the repo — read_file / search_code for real routes and env references. Do NOT invent features. When done, list the 3 saved artifacts.',
    ),
  },
  {
    key: '/settings',
    description: 'Open API keys, deploy targets, MCP servers, and artifacts.',
    action: (ctx) => { ctx.openSettings?.(); ctx.showToast?.('Settings…'); },
  },
  {
    key: '/help',
    description: 'Show all slash commands.',
    action: (ctx) => ctx.sendPrompt('/help'),
  },
];

/** Filter commands by prefix typed after the slash. */
export function matchSlashCommands(query) {
  const q = (query || '').toLowerCase();
  if (!q.startsWith('/')) return [];
  return SLASH_COMMANDS.filter((c) => c.key.toLowerCase().startsWith(q));
}
